import {
  OnboardingFormValues,
  getOnboardingDeclarationItems,
  getOnboardingTotalSteps,
  isScanOrPlusMembership
} from '../types/membersTypes';

/** Mirrors Step2 `validateStepFields` — not tied to Formik errors */
export function isStep2Complete(values: OnboardingFormValues): boolean {
  if (!values.overall_health?.value) return false;
  if (!values.activity_level?.value) return false;
  if (!values.exercise_routine?.value) return false;
  if (values.exercise_routine?.key === 'Other' && !values.other_exercise_routine?.trim()) {
    return false;
  }
  if (!values.smoking_status?.value) return false;
  if (!values.alcohol_frequency?.value) return false;
  if (!values.working_style?.value) return false;
  if (values.working_style?.key === 'Other' && !values.other_working_style?.trim()) return false;
  if (!values.diet_types?.length) return false;
  if (values.diet_types?.some((d) => d.key === 'Other') && !values.other_diet_type?.trim()) {
    return false;
  }
  if (!values.eating_pattern?.value) return false;
  if (values.eating_pattern?.key === 'Other' && !values.other_eating_pattern?.trim()) return false;
  if (!values.hydration?.value) return false;
  if (values.hydration?.key === 'Other' && !values.other_hydration?.trim()) return false;
  if (!values.calming_activities?.length) return false;
  if (values.calming_activities?.some((a) => a.key === 'Other') && !values.other_calming_activity?.trim()) {
    return false;
  }
  return true;
}

/** Resume check from persisted medical card (Step 2). */
export function isStep2CompleteFromMedicalCard(medicalCardData: {
  health_lifestyle?: Record<string, unknown>;
  nutritionist_questions?: Record<string, unknown>;
} | null): boolean {
  const health = medicalCardData?.health_lifestyle;
  const nutritionist = medicalCardData?.nutritionist_questions;
  if (!health?.overall_health || !health?.activity_level) return false;
  if (!nutritionist?.exercise_routine) return false;
  if (nutritionist.exercise_routine === 'Other' && !String(nutritionist.other_exercise_routine || '').trim()) {
    return false;
  }
  if (!health.smoking_status || !health.alcohol_frequency) return false;
  if (!nutritionist.working_style) return false;
  if (nutritionist.working_style === 'Other' && !String(nutritionist.other_working_style || '').trim()) {
    return false;
  }
  const dietTypes = nutritionist.diet_types;
  if (!Array.isArray(dietTypes) || dietTypes.length === 0) return false;
  if (dietTypes.includes('Other') && !String(nutritionist.other_diet_type || '').trim()) return false;
  if (!nutritionist.eating_pattern) return false;
  if (nutritionist.eating_pattern === 'Other' && !String(nutritionist.other_eating_pattern || '').trim()) {
    return false;
  }
  if (!nutritionist.hydration) return false;
  if (nutritionist.hydration === 'Other' && !String(nutritionist.other_hydration || '').trim()) return false;
  const calmingActivities = nutritionist.calming_activities;
  if (!Array.isArray(calmingActivities) || calmingActivities.length === 0) return false;
  if (calmingActivities.includes('Other') && !String(nutritionist.other_calming_activity || '').trim()) {
    return false;
  }
  return true;
}

/** Resume check from persisted medical card (Medical History). */
export function isMedicalHistoryCompleteFromMedicalCard(medicalCardData: {
  health_lifestyle?: {
    medical_conditions?: string | null;
    previous_surgeries?: string | null;
    allergies?: string | null;
    supplements?: string | null;
  };
} | null): boolean {
  const health = medicalCardData?.health_lifestyle;
  if (!health) return false;
  const fields = [
    health.medical_conditions,
    health.previous_surgeries,
    health.allergies,
    health.supplements
  ];
  return fields.every((value) => value !== undefined && value !== null);
}

export function getOnboardingResumeStep(
  medicalCardData: {
    basics?: { birth_date?: string; email?: string; phone?: string; height?: number; biological_sex?: string };
    health_lifestyle?: Record<string, unknown>;
    nutritionist_questions?: Record<string, unknown>;
    goals?: { health_concerns?: string[] };
    scan_info?: Record<string, unknown>;
    female_sex_hormones?: unknown;
  } | null,
  membershipType?: string | null
): { number: number; name: string } {
  const scanInfo = medicalCardData?.scan_info || {};
  const isScanOrPlus = isScanOrPlusMembership(membershipType);
  const isFemale = medicalCardData?.basics?.biological_sex === 'female';

  let nextStep = 1;
  let stepName = 'Basics';

  const basics = medicalCardData?.basics;
  if (basics?.birth_date && basics?.email && basics?.phone && basics?.height) {
    nextStep = 2;
    stepName = 'Current Lifestyle';

    if (isStep2CompleteFromMedicalCard(medicalCardData)) {
      if (isFemale) {
        const hormonesDone = !!scanInfo.onboarding_hormones_step_completed;
        nextStep = 3;
        stepName = 'Required: Female Sex Hormones';

        if (hormonesDone) {
          nextStep = 4;
          stepName = 'Medical History';

          if (isMedicalHistoryCompleteFromMedicalCard(medicalCardData)) {
            if (isScanOrPlus) {
              nextStep = 5;
              stepName = 'MRI Scan Safety and Clinical Considerations';

              if (scanInfo.reason_for_scan) {
                nextStep = 6;
                stepName = 'Goals';

                if (medicalCardData?.goals?.health_concerns?.length) {
                  nextStep = 7;
                  stepName = 'Declaration';
                }
              }
            } else {
              nextStep = 5;
              stepName = 'Goals';

              if (medicalCardData?.goals?.health_concerns?.length) {
                nextStep = 6;
                stepName = 'Declaration';
              }
            }
          }
        }
      } else {
        nextStep = 3;
        stepName = 'Medical History';

        if (isMedicalHistoryCompleteFromMedicalCard(medicalCardData)) {
          if (isScanOrPlus) {
            nextStep = 4;
            stepName = 'MRI Scan Safety and Clinical Considerations';

            if (scanInfo.reason_for_scan) {
              nextStep = 5;
              stepName = 'Goals';

              if (medicalCardData?.goals?.health_concerns?.length) {
                nextStep = 6;
                stepName = 'Declaration';
              }
            }
          } else {
            nextStep = 4;
            stepName = 'Goals';

            if (medicalCardData?.goals?.health_concerns?.length) {
              nextStep = 5;
              stepName = 'Declaration';
            }
          }
        }
      }
    }
  }

  return { number: nextStep, name: stepName };
}

export function isMedicalHistoryComplete(values: OnboardingFormValues): boolean {
  const requirePair = (
    answer: 'yes' | 'no' | undefined,
    detail: string | undefined
  ) => {
    if (answer !== 'yes' && answer !== 'no') return false;
    if (answer === 'yes' && !detail?.trim()) return false;
    return true;
  };

  return (
    requirePair(values.has_medical_conditions, values.medical_conditions_list) &&
    requirePair(values.has_surgeries, values.surgeries_details) &&
    requirePair(values.has_medications_supplements, values.supplements) &&
    requirePair(values.has_allergies, values.allergies)
  );
}

export function isMRISafetyComplete(values: OnboardingFormValues): boolean {
  if (!values.reason_for_scan?.trim()) return false;
  if (!values.current_symptoms?.trim()) return false;
  if (values.has_cancer_history !== 'yes' && values.has_cancer_history !== 'no') return false;
  if (values.has_cancer_history === 'yes' && !values.cancer_history_details?.trim()) return false;
  if (!values.medical_implants?.length) return false;
  const needsImplantDetails = values.medical_implants.some((i) => i.key !== 'None of the above');
  if (needsImplantDetails && !values.medical_implants_details?.trim()) return false;
  if (!values.practical_considerations?.length) return false;
  const needsPracticalDetails = values.practical_considerations.some(
    (i) => i.key !== 'None of the above'
  );
  if (needsPracticalDetails && !values.practical_considerations_details?.trim()) return false;
  return true;
}

export function isGoalsComplete(values: OnboardingFormValues): boolean {
  if (!values.health_concerns?.length) return false;
  const needsDetails = values.health_concerns.some((c) => c.key !== 'None');
  if (needsDetails && !values.health_concern_details?.trim()) return false;
  return true;
}

export function isDeclarationComplete(
  values: OnboardingFormValues,
  membershipType?: string | null
): boolean {
  const isFemale = values.gender?.key === 'female';
  return getOnboardingDeclarationItems(isFemale, membershipType).every(
    (item) => values.disclaimer_confirmations?.[item] === true
  );
}

export function isOnboardingStepComplete(
  stepNumber: number,
  values: OnboardingFormValues,
  membershipType?: string | null
): boolean {
  const isScanOrPlus = isScanOrPlusMembership(membershipType);
  const isFemale = values.gender?.key === 'female';

  if (stepNumber === 2) return isStep2Complete(values);

  if (isFemale) {
    if (stepNumber === 3) return true;
    if (stepNumber === 4) return isMedicalHistoryComplete(values);
    if (isScanOrPlus) {
      if (stepNumber === 5) return isMRISafetyComplete(values);
      if (stepNumber === 6) return isGoalsComplete(values);
      if (stepNumber === 7) return isDeclarationComplete(values, membershipType);
    } else {
      if (stepNumber === 5) return isGoalsComplete(values);
      if (stepNumber === 6) return isDeclarationComplete(values, membershipType);
    }
    return true;
  }

  if (stepNumber === 3) return isMedicalHistoryComplete(values);

  if (isScanOrPlus) {
    if (stepNumber === 4) return isMRISafetyComplete(values);
    if (stepNumber === 5) return isGoalsComplete(values);
    if (stepNumber === 6) return isDeclarationComplete(values, membershipType);
  } else {
    if (stepNumber === 4) return isGoalsComplete(values);
    if (stepNumber === 5) return isDeclarationComplete(values, membershipType);
  }

  return true;
}

export function shouldShowIncompleteStepReminder(
  stepNumber: number,
  values: OnboardingFormValues,
  membershipType?: string | null
): boolean {
  const isFemale = values.gender?.key === 'female';
  const maxStep = getOnboardingTotalSteps(membershipType, isFemale);
  if (stepNumber < 2 || stepNumber > maxStep) return false;
  return !isOnboardingStepComplete(stepNumber, values, membershipType);
}
